// src/services/historicTeachingService.ts
//
// Phase 6 (v1.4.0) scaffold.
//
// Read-side service over the `staff_teaching_history` cache table. The write-side
// (pulling from Synergetic and populating the cache) is a future edge function —
// this service and the UI it powers degrade gracefully to "no history yet" while
// the cache is empty.
//
// Shape: one row per (staff_code, academic_year, subject_code, year_level). We
// aggregate client-side into "Year 10 English × 3 years (2022–2024)" chips.

import { supabase } from '../supabase/config';

export interface TeachingHistoryRow {
  id: string;
  staffCode: string;
  academicYear: number;
  subjectCode: string | null;
  subjectName: string;
  yearLevel: string | null;
  fetchedAt: Date;
  source: string;
}

export interface TeachingHistorySummary {
  /** Display label, e.g. "Year 10 English" */
  label: string;
  /** Academic years this subject+year-level combo appeared in, newest-first */
  years: number[];
  /** Derived: number of distinct academic years, handy for chip badge */
  yearCount: number;
  /** Derived: oldest..newest range string, e.g. "2022–2024" */
  yearRange: string;
  /** Raw rows backing this summary, in case the UI wants to drill down */
  rows: TeachingHistoryRow[];
}

function mapRow(row: any): TeachingHistoryRow {
  return {
    id: row.id,
    staffCode: row.staff_code,
    academicYear: row.academic_year,
    subjectCode: row.subject_code,
    subjectName: row.subject_name,
    yearLevel: row.year_level,
    fetchedAt: row.fetched_at ? new Date(row.fetched_at) : new Date(),
    source: row.source || 'synergetic'
  };
}

function summarise(rows: TeachingHistoryRow[]): TeachingHistorySummary[] {
  const buckets = new Map<string, TeachingHistoryRow[]>();
  for (const row of rows) {
    // Group by subject + year level so "Year 10 English" and "Year 11 English" are separate
    const key = `${row.yearLevel || ''}::${row.subjectName}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(row);
  }

  const summaries: TeachingHistorySummary[] = [];
  for (const [, rowList] of buckets) {
    const years = [...new Set(rowList.map(r => r.academicYear))].sort((a, b) => b - a);
    const first = rowList[0];
    const label = first.yearLevel
      ? `${first.yearLevel} ${first.subjectName}`
      : first.subjectName;
    const minYear = years[years.length - 1];
    const maxYear = years[0];
    const yearRange = minYear === maxYear ? `${minYear}` : `${minYear}–${maxYear}`;
    summaries.push({
      label,
      years,
      yearCount: years.length,
      yearRange,
      rows: rowList
    });
  }

  // Sort summaries by: highest year count first, then most-recent year first
  summaries.sort((a, b) => {
    if (a.yearCount !== b.yearCount) return b.yearCount - a.yearCount;
    return b.years[0] - a.years[0];
  });
  return summaries;
}

export const historicTeachingService = {
  /**
   * Fetch raw history rows for a staff member, optionally restricted to the last N years.
   */
  async getHistoryForStaff(staffCode: string, yearsBack: number = 3): Promise<TeachingHistoryRow[]> {
    if (!staffCode) return [];
    const cutoffYear = new Date().getFullYear() - yearsBack;
    try {
      const { data, error } = await supabase
        .from('staff_teaching_history')
        .select('*')
        .eq('staff_code', staffCode)
        .gte('academic_year', cutoffYear)
        .order('academic_year', { ascending: false });

      if (error) {
        console.error('historicTeachingService.getHistoryForStaff error', error);
        return [];
      }
      return (data || []).map(mapRow);
    } catch (err) {
      console.error('historicTeachingService.getHistoryForStaff threw', err);
      return [];
    }
  },

  /**
   * Fetch + summarise history for a staff member into compact chips ready for the UI.
   */
  async getSummaryForStaff(staffCode: string, yearsBack: number = 3): Promise<TeachingHistorySummary[]> {
    const rows = await this.getHistoryForStaff(staffCode, yearsBack);
    return summarise(rows);
  },

  /**
   * Bulk fetch for a list of staff codes. Returns a Map<staffCode, summaries[]>.
   * Useful for TeacherAssignmentModal which renders many teachers at once.
   */
  async getSummariesForStaffList(
    staffCodes: string[],
    yearsBack: number = 3
  ): Promise<Map<string, TeachingHistorySummary[]>> {
    const result = new Map<string, TeachingHistorySummary[]>();
    if (staffCodes.length === 0) return result;

    const cutoffYear = new Date().getFullYear() - yearsBack;
    try {
      const { data, error } = await supabase
        .from('staff_teaching_history')
        .select('*')
        .in('staff_code', staffCodes)
        .gte('academic_year', cutoffYear);

      if (error) {
        console.error('historicTeachingService.getSummariesForStaffList error', error);
        return result;
      }
      // Group by staff_code, then summarise each group
      const byStaff = new Map<string, TeachingHistoryRow[]>();
      for (const row of (data || []).map(mapRow)) {
        if (!byStaff.has(row.staffCode)) byStaff.set(row.staffCode, []);
        byStaff.get(row.staffCode)!.push(row);
      }
      for (const [code, rows] of byStaff) {
        result.set(code, summarise(rows));
      }
      return result;
    } catch (err) {
      console.error('historicTeachingService.getSummariesForStaffList threw', err);
      return result;
    }
  }
};
